import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { MetaPage, PageCategory, UpdatePageConfigInput } from '../types/meta.types';
import { TIMEZONES, LANGUAGES, CURRENCIES } from '../constants/config-options';
import PageVariablesPanel from './PageVariablesPanel';

type DialogTab = 'config' | 'variables';

interface ConfigurePageDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    page: MetaPage | null;
    categories: PageCategory[];
    onSave: (pageId: number, data: UpdatePageConfigInput) => void;
    isLoading: boolean;
}

export default function ConfigurePageDialog({
    open,
    onOpenChange,
    page,
    categories,
    onSave,
    isLoading,
}: ConfigurePageDialogProps) {
    const [activeTab, setActiveTab] = useState<DialogTab>('config');
    const [formData, setFormData] = useState<UpdatePageConfigInput>({
        timezone: 'UTC',
        currency: 'USD',
        language: 'pt_BR',
        internal_category_id: null,
    });

    useEffect(() => {
        if (page) {
            setFormData({
                timezone: page.timezone || 'UTC',
                currency: page.currency || 'USD',
                language: page.language || 'pt_BR',
                internal_category_id: page.internal_category_id,
            });
        }
        setActiveTab('config');
    }, [page, open]);

    const handleSave = () => {
        if (page) {
            onSave(page.id, formData);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px] bg-white rounded-2xl border-none shadow-2xl">
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-[#0f172a]">
                        Configurar Página
                    </DialogTitle>
                    <p className="text-sm text-gray-500">{page?.page_name}</p>
                </DialogHeader>

                {/* Tabs */}
                <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
                    <button
                        onClick={() => setActiveTab('config')}
                        className={`flex-1 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'config'
                                ? 'bg-white text-[#0f172a] shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Configurações
                    </button>
                    <button
                        onClick={() => setActiveTab('variables')}
                        className={`flex-1 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'variables'
                                ? 'bg-white text-[#0f172a] shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Variáveis
                    </button>
                </div>

                {activeTab === 'config' && (
                    <div className="grid gap-5 py-2">
                        {/* Timezone */}
                        <div className="grid gap-2">
                            <Label htmlFor="timezone" className="text-sm font-semibold text-gray-700">
                                Fuso Horário
                            </Label>
                            <Select
                                value={formData.timezone}
                                onValueChange={(value) => setFormData({ ...formData, timezone: value })}
                            >
                                <SelectTrigger id="timezone" className="rounded-xl border-gray-200">
                                    <SelectValue placeholder="Selecione o fuso horário" />
                                </SelectTrigger>
                                <SelectContent className="bg-white rounded-xl border-gray-100 shadow-xl">
                                    {TIMEZONES.map((tz) => (
                                        <SelectItem key={tz.value} value={tz.value} className="rounded-lg">
                                            {tz.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Currency */}
                        <div className="grid gap-2">
                            <Label htmlFor="currency" className="text-sm font-semibold text-gray-700">
                                Moeda
                            </Label>
                            <Select
                                value={formData.currency}
                                onValueChange={(value) => setFormData({ ...formData, currency: value })}
                            >
                                <SelectTrigger id="currency" className="rounded-xl border-gray-200">
                                    <SelectValue placeholder="Selecione a moeda" />
                                </SelectTrigger>
                                <SelectContent className="bg-white rounded-xl border-gray-100 shadow-xl">
                                    {CURRENCIES.map((c) => (
                                        <SelectItem key={c.value} value={c.value} className="rounded-lg">
                                            {c.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Language */}
                        <div className="grid gap-2">
                            <Label htmlFor="language" className="text-sm font-semibold text-gray-700">
                                Idioma
                            </Label>
                            <Select
                                value={formData.language}
                                onValueChange={(value) => setFormData({ ...formData, language: value })}
                            >
                                <SelectTrigger id="language" className="rounded-xl border-gray-200">
                                    <SelectValue placeholder="Selecione o idioma" />
                                </SelectTrigger>
                                <SelectContent className="bg-white rounded-xl border-gray-100 shadow-xl">
                                    {LANGUAGES.map((lang) => (
                                        <SelectItem key={lang.value} value={lang.value} className="rounded-lg">
                                            {lang.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Category */}
                        <div className="grid gap-2">
                            <Label htmlFor="category" className="text-sm font-semibold text-gray-700">
                                Categoria Interna
                            </Label>
                            <Select
                                value={formData.internal_category_id?.toString() || 'none'}
                                onValueChange={(value) => setFormData({
                                    ...formData,
                                    internal_category_id: value === 'none' ? null : parseInt(value)
                                })}
                            >
                                <SelectTrigger id="category" className="rounded-xl border-gray-200">
                                    <SelectValue placeholder="Selecione uma categoria" />
                                </SelectTrigger>
                                <SelectContent className="bg-white rounded-xl border-gray-100 shadow-xl">
                                    <SelectItem value="none" className="rounded-lg text-gray-400">
                                        Nenhuma
                                    </SelectItem>
                                    {categories.map((cat) => (
                                        <SelectItem key={cat.id} value={cat.id.toString()} className="rounded-lg">
                                            {cat.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}

                {activeTab === 'variables' && page && (
                    <div className="py-2">
                        <PageVariablesPanel page={page} />
                    </div>
                )}

                {activeTab === 'config' && (
                    <DialogFooter>
                        <Button
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            className="rounded-xl"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={isLoading}
                            className="bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-xl px-8"
                        >
                            {isLoading ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
}
